import CookieService from "../services/cookie.service";
import config from "../constants/config";

const { ENV_SUFIX } = config;

/**
 * Gets client ID from cookies
 * @returns {string|null} - Client ID or null
 */
const getClientId = () => {
  try {
    const rpClientId = CookieService.getCookie("rp_clientid");
    if (rpClientId) {
      return rpClientId;
    }

    const authData = CookieService.getCookie("cip_authdata");
    if (!authData) {
      return null;
    }

    const decoded = atob(authData);
    const parsed = JSON.parse(decoded);
    return parsed?.client_id || null;
  } catch (error) {
    console.error("[AuditLogger] Error decoding cip_authdata:", error);
    return null;
  }
};

/**
 * Gets site identifier based on hostname
 * @returns {string} - 'China' or 'US'
 */
const getSite = () => {
  return window.location.host.includes("thermofisher.cn") ? "China" : "US";
};

/**
 * Masks phone number for privacy (shows only last 4 digits)
 * @param {string} phoneNumber - Phone number to mask
 * @returns {string} - Masked phone number
 */
const maskPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber === "NA") {
    return "NA";
  }
  return phoneNumber.slice(-4);
};

/**
 * Logs audit data to the audit service
 * @param {string} userAction - Action being performed (e.g., "Email Login", "Re-Auth Widget")
 * @param {string} processStatus - Status of the process (e.g., "Login Success", "Login Failed")
 * @param {object} responseData - Response data object containing UID, email, errorCode, etc.
 * @param {string} site - Site identifier (China/US) - defaults to auto-detected
 * @returns {Promise<string|null>} - Audit ID or null
 */
export const logAuditData = async (
  userAction,
  processStatus,
  responseData = {},
  site = getSite(),
) => {
  // First try window.logAuditData for backward compatibility
  if (window.logAuditData && typeof window.logAuditData === "function") {
    try {
      return window.logAuditData(userAction, processStatus, responseData, site);
    } catch (e) {
      // Fall through to native implementation
    }
  }

  try {
    const auditLogUrl = process.env[`REACT_APP_AUDIT_LOG${ENV_SUFIX}`];

    if (!auditLogUrl) {
      console.warn("[AuditLogger] Audit log URL not configured");
      return null;
    }

    const clientId = getClientId() || "NA";
    const isoCode = CookieService.getCookie("CK_ISO_CODE") || "us";
    const userEmail =
      responseData?.user?.email || responseData?.profile?.email || "NA";

    let errorCode = 0;
    if (
      responseData?.errorCode !== undefined &&
      responseData?.errorCode !== null
    ) {
      const parsed = parseInt(responseData.errorCode, 10);
      errorCode = isNaN(parsed) ? 0 : parsed;
    }

    const payload = {
      clientId: String(clientId || "NA"),
      country: String(isoCode || "NA"),
      uid: String(responseData?.UID || "NA"),
      userName: String(userEmail || "NA"),
      email: String(userEmail || "NA"),
      phoneNumber: String(maskPhoneNumber(responseData?.phoneNumber || "NA")),
      site: String(site || "NA"),
      userAction: String(userAction || "NA"),
      processStatus: String(processStatus || "NA"),
      responseCode: errorCode,
      errorMessage: String(responseData?.errorMessage || "NA"),
      requestPayload: window.location.hostname || "NA",
    };

    const response = await fetch(auditLogUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const data = await response.json();
      return data.auditId || null;
    } else {
      console.error(
        "[AuditLogger] Failed to save transaction:",
        response.status,
      );
      return null;
    }
  } catch (error) {
    console.error("[AuditLogger] Failed to save transaction:", error);
    return null;
  }
};

/**
 * Logs profile change audit data
 * @param {string} action - Action being performed
 * @param {string} prevValue - Previous value
 * @param {string} newValue - New value
 * @param {object} data - Additional data
 * @param {string} site - Site identifier
 * @returns {Promise<string|null>} - Audit ID or null
 */
export const logProfileChangeAudit = async (
  action,
  prevValue,
  newValue,
  data = {},
  site = getSite(),
) => {
  // First try window.logProfileChangeAudit for backward compatibility
  if (
    window.logProfileChangeAudit &&
    typeof window.logProfileChangeAudit === "function"
  ) {
    try {
      return window.logProfileChangeAudit(
        action,
        prevValue,
        newValue,
        data,
        site,
      );
    } catch (e) {
      // Fall through to native implementation
    }
  }

  // Use logAuditData with profile change specific data
  const profileChangeData = {
    ...data,
    previousValue: prevValue,
    newValue: newValue,
  };

  return logAuditData(
    `Profile Change: ${action}`,
    "Profile Updated",
    profileChangeData,
    site,
  );
};

/**
 * Helper to create audit response data from Gigya response
 * @param {object} gigyaResponse - Response from Gigya API
 * @returns {object} - Formatted audit data
 */
export const formatGigyaResponseForAudit = (gigyaResponse) => {
  return {
    UID: gigyaResponse?.UID || "NA",
    errorCode: gigyaResponse?.errorCode || 0,
    errorMessage: gigyaResponse?.errorMessage || "NA",
    user: {
      email: gigyaResponse?.profile?.email || gigyaResponse?.loginID || "NA",
    },
    phoneNumber: gigyaResponse?.phoneNumber || "NA",
  };
};

const auditLogger = {
  logAuditData,
  logProfileChangeAudit,
  formatGigyaResponseForAudit,
  getSite,
  getClientId,
};

export default auditLogger;
